import { Checkbox } from 'antd';
import { useFormikContext } from 'formik';
import styled from 'styled-components';

export type Permission = {
  code: string;
  name?: string;
  isView?: boolean | null;
  isUpdate?: boolean | null;
  isDelete?: boolean | null;
  isCreate?: boolean | null;
  isReview?: boolean | null;
  isActive?: boolean | null;
  hasAccess?: boolean | null;
};

type Props = { data?: Permission[] };
type PermissionValues = { rolePermissions?: Permission[] };
const columns = [
  { key: 'isView', label: 'Харах' },
  { key: 'isCreate', label: 'Үүсгэх' },
  { key: 'isUpdate', label: 'Засах' },
  { key: 'isDelete', label: 'Устгах' },
] as const;

const PermissionTable = ({ data = [] }: Props) => {
  const { values, setValues } = useFormikContext<PermissionValues>();

  const togglePermission = (item: Permission, checked: boolean) => {
    setValues((previous) => {
      const current = previous.rolePermissions || [];

      const remaining = current.filter((permission) => permission.code !== item.code);
      if (!checked) return { ...previous, rolePermissions: remaining };
      const permission: Permission = { code: item.code, hasAccess: true };
      if (!(item.isCreate === null && item.isDelete === null)) {
        for (const { key } of columns) {
          if (item[key] !== null) permission[key] = true;
        }
      }
      return { ...previous, rolePermissions: [...remaining, permission] };
    });
  };

  const toggleFlag = (code: string, flag: typeof columns[number]['key'], checked: boolean) => {
    setValues((previous) => ({
      ...previous,
      rolePermissions: (previous.rolePermissions || []).map((permission) =>
        permission.code === code ? { ...permission, [flag]: checked } : permission
      ),
    }));
  };

  return (
    <Wrap>
      <table className="perm-matrix">
        <thead>
          <tr>
            <th className="left">Хандах цэс</th>
            {columns.map(({ key, label }) => <th key={key}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const selected = values.rolePermissions?.find((permission) => permission.code === item.code);
            const accessOnly = item.isCreate === null && item.isDelete === null;
            const enabled = !!selected;
            return (
              <tr key={item.code}>
                <td className="left">
                  <Checkbox
                    checked={accessOnly ? enabled && selected.hasAccess !== false : enabled}
                    onChange={(event) => togglePermission(item, event.target.checked)}
                  >
                    {item.name || item.code}
                  </Checkbox>
                </td>
                {columns.map(({ key, label }) => (
                  <td key={key} className={accessOnly || item[key] === null ? 'center bg-[#E5E7EB]' : 'center'}>
                    {accessOnly || item[key] === null ? '-' : (
                      <Checkbox
                        aria-label={`${item.name || item.code}: ${label}`}
                        checked={!!selected?.[key]}
                        disabled={!enabled}
                        onChange={(event) => toggleFlag(item.code, key, event.target.checked)}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Wrap>
  );
};

const Wrap = styled.div`
  .perm-matrix {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, 'Noto Sans',
      'Helvetica Neue', sans-serif;
    color: #111827;
  }
  .perm-matrix th,
  .perm-matrix td {
    border: 1px solid #e5e7eb;
    padding: 10px;
  }
  .perm-matrix th {
    background: #f9fafb;
    font-weight: 600;
    font-size: 14px;
  }
  .perm-matrix td {
    font-size: 14px;
  }
  .left {
    text-align: left;
  }
  .center {
    text-align: center;
  }
  .th-cell,
  .row-head {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
  }
  .row-head {
    justify-content: flex-start;
  }
  @media (max-width: 640px) {
    .perm-matrix th,
    .perm-matrix td {
      padding: 8px;
      font-size: 13px;
    }
  }
`;

export { PermissionTable };
